import { useState, useEffect, useCallback } from 'react';
import type { Card, Flow, ConfiguracaoCardConectado } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { apiUrl } from '../../utils/apiBase';

interface ModalCriarCardConectadoProps {
  card: Card;
  flow: Flow;
  session?: any;
  onClose: () => void;
  onCardCriado: (novoCard: any, relacao: any, abrirParaEditar?: boolean) => void;
}

const ModalCriarCardConectado = ({ card, flow, session, onClose, onCardCriado }: ModalCriarCardConectadoProps) => {
  const { success, error: toastError } = useToast();
  const [configuracoes, setConfiguracoes] = useState<ConfiguracaoCardConectado[]>([]);
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<string>('');
  const [titulo, setTitulo] = useState('');

  const getAuthParams = useCallback(() => {
    const params = new URLSearchParams();
    if (session?.usuario?.email) params.set('userEmail', session.usuario.email);
    if (session?.empresa?.id) params.set('empresaId', session.empresa.id);
    return params.toString();
  }, [session]);

  const getAuthHeaders = useCallback(() => {
    return { 'Content-Type': 'application/json' };
  }, []);

  useEffect(() => {
    const carregarConfigs = async () => {
      try {
        const headers = getAuthHeaders();
        const res = await fetch(`${apiUrl(`/cards-conectados/configuracoes/fluxo/${flow.id}`)}?${getAuthParams()}`, { headers });
        const data = await res.json();
        
        if (data.success) {
          const todas = data.configuracoes || [];
          console.log('🔗 Configs recebidas:', todas);
          console.log('🔗 flow.id:', flow.id, 'card.etapaId:', card.etapaId);
          
          // Filtrar: configs onde este fluxo é origem
          // Usar String() para evitar problemas de tipo (number vs string)
          const configsDoFluxo = todas.filter((c: ConfiguracaoCardConectado) => {
            const matchFluxo = String(c.fluxoOrigemId) === String(flow.id);
            const matchEtapa = String(c.etapaOrigemId) === String(card.etapaId);
            console.log('🔗 Config:', c.nome, 
              '| fluxoOrigemId:', c.fluxoOrigemId, '==', flow.id, '?', matchFluxo,
              '| etapaOrigemId:', c.etapaOrigemId, '==', card.etapaId, '?', matchEtapa);
            return matchFluxo;
          });
          
          // Priorizar as que batem com a etapa, mas mostrar todas do fluxo
          const configsEtapaExata = configsDoFluxo.filter((c: ConfiguracaoCardConectado) => 
            String(c.etapaOrigemId) === String(card.etapaId)
          );
          
          const configsFinais = configsEtapaExata.length > 0 ? configsEtapaExata : configsDoFluxo;
          setConfiguracoes(configsFinais);
          if (configsFinais.length === 1) {
            setSelectedConfig(configsFinais[0].id);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar configurações:', err);
      } finally {
        setLoading(false);
      }
    };
    carregarConfigs();
  }, [flow.id, card.etapaId, getAuthHeaders]);

  const handleCriar = async () => {
    if (!selectedConfig) {
      toastError('Selecione uma configuração');
      return;
    }

    try {
      setCriando(true);
      const headers = getAuthHeaders();
      
      const res = await fetch(`${apiUrl('/cards-conectados/criar')}?${getAuthParams()}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          configuracaoId: selectedConfig,
          cardOrigemId: card.id,
          flowOrigemId: flow.id,
          titulo: titulo || undefined
        })
      });
      
      const data = await res.json();
      if (data.success) {
        const config = configuracoes.find(c => c.id === selectedConfig);
        const deveAbrir = config?.obrigarPreenchimento === true;
        success(`Card conectado criado! ${data.message || ''}${deveAbrir ? ' Preencha os campos obrigatórios.' : ''}`);
        onCardCriado(data.card, data.relacao, deveAbrir);
        onClose();
      } else {
        if (data.condicoesNaoAtendidas) {
          toastError('As condições desta configuração não foram atendidas pelo card atual.');
        } else {
          toastError(data.error || 'Erro ao criar card conectado');
        }
      }
    } catch (err) {
      toastError('Erro ao criar card conectado');
    } finally {
      setCriando(false);
    }
  };

  const configSelecionada = configuracoes.find(c => c.id === selectedConfig);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                {configSelecionada?.tipo === 'mover_existente' ? 'Mover Card Conectado' : 'Criar Card Conectado'}
              </h2>
              <p className="text-sm text-gray-500">
                {configSelecionada?.tipo === 'mover_existente'
                  ? `Mover card existente conectado a "${card.titulo}"`
                  : `Gerar subprocesso a partir de "${card.titulo}"`}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-500">Carregando configurações...</span>
            </div>
          ) : configuracoes.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <p className="text-gray-500 text-sm mb-1">Nenhuma configuração disponível para esta etapa.</p>
              <p className="text-gray-400 text-xs">
                Configure cards conectados no menu Fluxos → Cards Conectados.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Seleção de configuração */}
              {configuracoes.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Configuração de Conexão
                  </label>
                  <select
                    value={selectedConfig}
                    onChange={e => setSelectedConfig(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Selecione...</option>
                    {configuracoes.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Preview da conexão */}
              {configSelecionada && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  {configSelecionada.tipo === 'mover_existente' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-2 mb-2 text-xs text-amber-800">
                      <strong>Mover existente:</strong> O card anteriormente conectado será movido para o fluxo/etapa de destino.
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs mb-2">
                    <div className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded font-medium">
                      {flow.name}
                    </div>
                    <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                    <div className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-medium">
                      {configSelecionada.tipo === 'mover_existente' ? 'Mover para destino' : 'Fluxo de destino'}
                    </div>
                  </div>
                  <div className="flex gap-3 text-[10px] text-gray-500">
                    {configSelecionada.copiarHistorico && <span>📋 Copia histórico</span>}
                    {configSelecionada.copiarAnexos && <span>📎 Copia anexos</span>}
                    <span style={{ color: configSelecionada.corDestaque }}>● Cor: {configSelecionada.corDestaque}</span>
                  </div>

                  {/* Condições configuradas */}
                  {configSelecionada.condicoes && configSelecionada.condicoes.length > 0 && (
                    <div className="mt-2 bg-violet-50 border border-violet-200 rounded-md p-2">
                      <div className="text-[10px] font-semibold text-violet-700 mb-1 flex items-center gap-1">
                        🔍 Condições ({configSelecionada.condicoesLogica || 'E'})
                      </div>
                      <div className="space-y-0.5">
                        {configSelecionada.condicoes.map((cond: any, i: number) => {
                          const nomesCampos: Record<string, string> = {
                            '__responsavel__': 'Responsável',
                            '__criador__': 'Criador', 
                            '__titulo__': 'Título'
                          };
                          const nomesOps: Record<string, string> = {
                            'igual': '=', 'diferente': '≠', 'contem': '∋', 
                            'nao_contem': '∌', 'vazio': 'vazio', 'nao_vazio': '≠ vazio'
                          };
                          return (
                            <div key={i} className="text-[10px] text-violet-600">
                              {i > 0 && <span className="font-bold mr-1">{configSelecionada.condicoesLogica || 'E'}</span>}
                              {nomesCampos[cond.campo] || cond.campo} {nomesOps[cond.operador] || cond.operador} {cond.valor || ''}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Título personalizado - apenas para modo criar */}
              {(!configSelecionada || configSelecionada.tipo !== 'mover_existente') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Título do Card Conectado (opcional)
                </label>
                <input
                  type="text"
                  value={titulo}
                  onChange={e => setTitulo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={configSelecionada ? `${configSelecionada.prefixoTitulo || '[CONECTADO]'} ${card.titulo}` : ''}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Deixe vazio para usar o título automático com prefixo
                </p>
              </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            Cancelar
          </button>
          {configuracoes.length > 0 && (
            <button
              onClick={handleCriar}
              disabled={criando || !selectedConfig}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {criando ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {configSelecionada?.tipo === 'mover_existente' ? 'Movendo...' : 'Criando...'}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {configSelecionada?.tipo === 'mover_existente' ? 'Mover Card Conectado' : 'Criar Card Conectado'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalCriarCardConectado;
